// Pure economy formulas. No state, no side effects: safe to unit-test and simulate.
import { D, Decimal, ZERO } from '../core/decimal';
import { BALANCE } from '../data/balance';
import type { HeroDef } from '../data/heroes';

export function monsterHp(stage: number): Decimal {
  const { base, bands } = BALANCE.monsterHp;
  // Growth is applied once per stage step from 1 -> stage, using each band's rate.
  let hp = D(base);
  for (let i = 0; i < bands.length; i++) {
    const band = bands[i]!;
    const end = bands[i + 1]?.fromStage ?? Infinity;
    const steps = Math.min(stage, end) - Math.max(band.fromStage, 1);
    if (steps <= 0) break;
    hp = hp.mul(Decimal.pow(band.growth, steps));
  }
  return hp.ceil();
}

export function bossHp(stage: number): Decimal {
  return monsterHp(stage).mul(BALANCE.bossHpMult);
}

export function monsterGold(stage: number): Decimal {
  return monsterHp(stage).mul(BALANCE.monsterGold.hpRatio).add(BALANCE.monsterGold.flat).ceil();
}

/** Cost of buying `count` levels starting at `level`. */
export function heroCost(hero: HeroDef, level: number, count = 1): Decimal {
  return Decimal.sumGeometricSeries(count, hero.baseCost, BALANCE.heroCostGrowth, level);
}

/** How many levels `gold` can buy right now. */
export function heroAffordable(hero: HeroDef, level: number, gold: Decimal): number {
  return Decimal.affordGeometricSeries(gold, hero.baseCost, BALANCE.heroCostGrowth, level).toNumber();
}

export function heroDps(hero: HeroDef, level: number): Decimal {
  if (level <= 0) return ZERO;
  const milestones = Math.floor(level / BALANCE.heroMilestoneEvery);
  return D(hero.baseDps).mul(level).mul(Decimal.pow(2, milestones));
}

export function tapCost(level: number, count = 1): Decimal {
  return Decimal.sumGeometricSeries(count, BALANCE.tapCost.base, BALANCE.tapCost.growth, level).ceil();
}

export function tapAffordable(level: number, gold: Decimal): number {
  return Decimal.affordGeometricSeries(gold, BALANCE.tapCost.base, BALANCE.tapCost.growth, level).toNumber();
}

/** Tap damage from Sword Master levels alone, before hero share and multipliers. */
export function tapBase(level: number): Decimal {
  const { base, perLevelGrowth } = BALANCE.tapDamage;
  return D(1 + level).mul(base).mul(Decimal.pow(perLevelGrowth, level));
}

export function relicsForStage(stage: number): Decimal {
  const { minStage, offset, coef, power, growth, lateFrom, lateGrowth } = BALANCE.prestige;
  if (stage < minStage) return ZERO;
  const early = Math.min(stage, lateFrom), late = Math.max(0, stage - lateFrom);
  return D(coef).mul(Math.pow(stage - offset, power)).mul(Decimal.pow(growth, early)).mul(Decimal.pow(lateGrowth, late)).floor().max(1);
}
